import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntToolbar from '../../components/enterprise/EntToolbar';
import { roleLabel } from '../../utils/companyRoles';
import { formatDate, isValidEmail, normalizeEmail, seatLabel } from '../../utils/enterpriseUi';

export default function EnterpriseInvitesPage() {
  const { enterpriseId } = useParams();
  const { showSuccess, showError } = useToast();
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState('');
  const [seatTier, setSeatTier] = useState('team_basic');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('pending');
  const [query, setQuery] = useState('');
  const [formError, setFormError] = useState('');

  const load = (nextView = view) => {
    const params = { enterpriseId };
    if (nextView === 'expired') params.includeExpired = 'true';
    if (nextView === 'all') params.includeAll = 'true';
    return api.get('/api/enterprise/invites', { params }).then((res) => {
      setInvites(res.data.invites || []);
    });
  };

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    const params = { enterpriseId };
    if (view === 'expired') params.includeExpired = 'true';
    if (view === 'all') params.includeAll = 'true';
    Promise.all([
      api.get('/api/enterprise/invites', { params }),
      api.get('/api/enterprise/teams', { params: { enterpriseId } }).catch(() => ({ data: { teams: [] } })),
      api.get('/api/enterprise/seats', { params: { enterpriseId } }).catch(() => ({ data: { capacity: null } })),
    ])
      .then(([invitesRes, teamsRes, seatsRes]) => {
        if (cancelled) return;
        setInvites(invitesRes.data.invites || []);
        setTeams(teamsRes.data.teams || []);
        setCapacity(seatsRes.data.capacity || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load invites');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, view]);

  const hasPool = capacity?.source === 'entitlement' && ((capacity?.members?.total || 0) > 0 || (capacity?.purchased?.total || 0) > 0);
  const memberPlacesRemaining = hasPool
    ? Math.max(0, (capacity?.members?.remaining ?? capacity?.remaining?.total ?? 0) - (capacity?.pendingInvites || 0))
    : null;
  const proLicencesRemaining = hasPool ? (capacity?.proLicences?.remaining ?? capacity?.remaining?.team_pro ?? 0) : null;
  const remainingForTier = seatTier === 'team_pro'
    ? Math.min(memberPlacesRemaining ?? 0, proLicencesRemaining ?? 0)
    : (memberPlacesRemaining ?? 0);
  const pendingSameEmail = invites.some(
    (invite) => invite.status === 'pending' && normalizeEmail(invite.email) === normalizeEmail(email)
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return invites.filter((invite) => {
      if (!needle) return true;
      return `${invite.email} ${invite.teamName || ''} ${invite.inviterName || ''}`.toLowerCase().includes(needle);
    });
  }, [invites, query]);

  const handleSendInvite = (e) => {
    e.preventDefault();
    const normalized = normalizeEmail(email);
    setFormError('');
    if (!isValidEmail(normalized)) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (pendingSameEmail) {
      setFormError('A pending invite already exists for this email.');
      return;
    }
    if (hasPool && remainingForTier < 1) {
      setFormError(
        seatTier === 'team_pro'
          ? 'No remaining Teams Pro capacity. Free a Pro licence or invite as Teams Basic.'
          : 'No remaining member places. Free a place or ask the owner to add capacity.'
      );
      return;
    }
    setSending(true);
    api
      .post('/api/enterprise/invite', {
        enterpriseId,
        email: normalized,
        teamId: teamId || undefined,
        seatTier,
      })
      .then(() => {
        showSuccess('Invite sent. They join as a Member.');
        setEmail('');
        return load(view);
      })
      .catch((err) => {
        showError(err.response?.data?.message || err.response?.data?.error || 'Failed to send invite');
      })
      .finally(() => setSending(false));
  };

  const statusChip = (status) => {
    const label = status === 'expired' ? 'Expired' : status === 'accepted' ? 'Accepted' : status === 'cancelled' ? 'Cancelled' : 'Pending';
    return <span className={`ent-chip ent-chip-${(status || 'pending').toLowerCase()}`}>{label}</span>;
  };

  const hero = (
      <EntHero
        title="Invitations"
        lead="Invite people by email. They join as Members. Choose Teams Basic or Teams Pro; a member place is reserved from your included capacity."
      />
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="rows" count={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      <section className="ent-section">
        <h2 className="ent-section-title">Send invite</h2>
        <form onSubmit={handleSendInvite} className="ent-panel">
          <div className="ent-inline-form">
            <div className="ent-form-group">
              <label htmlFor="invite-email">Email</label>
              <input
                id="invite-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFormError('');
                }}
                placeholder="colleague@company.com"
              />
            </div>
            <div className="ent-form-group">
              <label htmlFor="invite-team">Team (optional)</label>
              <select
                id="invite-team"
                className="ent-select"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value="">No team yet</option>
                {teams.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="ent-form-group">
              <label htmlFor="invite-seat">Access</label>
              <select
                id="invite-seat"
                className="ent-select"
                value={seatTier}
                onChange={(e) => setSeatTier(e.target.value)}
              >
                <option value="team_basic">Teams Basic</option>
                <option value="team_pro">Teams Pro</option>
              </select>
            </div>
            <button type="submit" className="ent-btn-primary" disabled={sending || !email.trim()}>
              {sending ? 'Sending…' : 'Send invite'}
            </button>
          </div>
          {formError ? <p className="ent-form-error" style={{ marginTop: 12 }}>{formError}</p> : null}
          <p className="ent-helper">
            Role is always Member from this page. Admin access is not granted by invitation.
            {hasPool
              ? ` ${memberPlacesRemaining} member ${memberPlacesRemaining === 1 ? 'place' : 'places'} remaining · ${capacity?.proLicences?.assigned || 0} Pro ${capacity?.proLicences?.assigned === 1 ? 'licence' : 'licences'} assigned · ${proLicencesRemaining} Pro ${proLicencesRemaining === 1 ? 'licence' : 'licences'} available.`
              : ' No Teams capacity is recorded yet, so this invite will not reserve a member place.'}
          </p>
        </form>
      </section>

      <section className="ent-section">
        <div className="ent-section-header">
          <h2 className="ent-section-title">Invites</h2>
        </div>
        <EntToolbar
          search={query}
          onSearch={setQuery}
          searchPlaceholder="Search email, team or inviter"
          searchLabel="Search invites"
        >
          <select
            className="ent-select"
            value={view}
            onChange={(e) => setView(e.target.value)}
            aria-label="Invitation status"
          >
            <option value="pending">Pending</option>
            <option value="expired">Pending and expired</option>
            <option value="all">All statuses</option>
          </select>
        </EntToolbar>

        {visible.length === 0 ? (
          <EntEmptyState
            title={invites.length === 0 ? 'No invitations yet' : 'No invitations match'}
            lead={
              invites.length === 0
                ? 'Send an email above. Pending invites appear here until they are accepted or they expire.'
                : 'Try a different search or status filter.'
            }
          />
        ) : (
          <>
            <div className="ent-table-wrap ent-hide-sm">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Team</th>
                    <th>Seat</th>
                    <th>Invited by</th>
                    <th>Sent</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((i) => (
                    <tr key={i._id}>
                      <td>{i.email}</td>
                      <td>{statusChip(i.status)}</td>
                      <td>{roleLabel(i)}</td>
                      <td>{i.teamName || '—'}</td>
                      <td>{seatLabel(i.seatTier)}</td>
                      <td>{i.inviterName || '—'}</td>
                      <td>{formatDate(i.createdAt)}</td>
                      <td>{formatDate(i.expiresAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ent-stack">
              {visible.map((i) => (
                <div key={i._id} className="ent-stack-card ent-stack-card-static">
                  <strong>{i.email}</strong>
                  <div className="ent-stack-meta">
                    {statusChip(i.status)}
                    <span>{roleLabel(i)}</span>
                    <span>{i.teamName || 'No team'}</span>
                    <span>{seatLabel(i.seatTier)}</span>
                    <span>Expires {formatDate(i.expiresAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
